import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Lock from '@mui/icons-material/Lock';

import { passwordRules } from '@shared/constants/validation.constants';
import { UnauthenticatedPath } from '@shared/constants/routes.constants';
import { useResetPassword } from '../hook/useResetPassword';
import { useStyles } from './styles';

export const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    onSubmit,
    control,
    errorMsg,
    successMSg,
    isLoading,
    getValues,
    formState: { errors },
  } = useResetPassword();

  const handleBackBtn = () => {
    navigate(UnauthenticatedPath.SIGN_IN);
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (!successMSg) return;

    const timeoutId = setTimeout(() => {
      navigate(UnauthenticatedPath.SIGN_IN);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [navigate, successMSg]);

  return (
    <form onSubmit={onSubmit}>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography className={classes.title} variant="h3" component="h1">
            {t('forgot.resetPassword.title')}
          </Typography>
          <Typography className={classes.description} component="div">
            {t('forgot.resetPassword.description')}
          </Typography>
          {errorMsg && (
            <Alert severity="error" role="alert" aria-live="polite">
              {errorMsg}
            </Alert>
          )}
          {successMSg && (
            <Alert severity="success" role="status" aria-live="polite">
              {successMSg}
            </Alert>
          )}
        </Box>
        <Box className={classes.main}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={passwordRules}
            render={({ field }) => (
              <TextField
                fullWidth
                id="reset-password"
                label={t('forgot.resetPassword.password')}
                placeholder={t('forgot.resetPassword.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                size="small"
                error={!!errors.password}
                helperText={errors.password ? errors.password.message : null}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                      {/* OR Use VpnKey if you prefer: <VpnKey /> */}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('forgot.resetPassword.togglePassword')}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...field}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: t('forgot.resetPassword.confirmPasswordRequired'),
              validate: (value) =>
                value === getValues('password') || t('forgot.resetPassword.passwordsMustMatch'),
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                id="reset-confirm-password"
                label={t('forgot.resetPassword.confirmPassword')}
                placeholder={t('forgot.resetPassword.confirmPasswordPlaceholder')}
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                size="small"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message ?? ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('forgot.resetPassword.toggleConfirmPassword')}
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...field}
              />
            )}
          />
        </Box>
        <Box className={classes.footer}>
          <Button
            disabled={isLoading || Boolean(successMSg)}
            fullWidth
            variant="contained"
            type="submit">
            {isLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
                aria-label={t('forgot.resetPassword.loading')}
              />
            ) : (
              t('forgot.resetPassword.submit')
            )}
          </Button>
          <Button fullWidth variant="contained" type="button" onClick={handleBackBtn}>
            {t('forgot.resetPassword.backToSignIn')}
          </Button>
        </Box>
      </Box>
    </form>
  );
};
